import { useState } from 'react';
import { Sparkles, Loader2, Copy, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { aiDriverAssistant } from '../lib/ai';
import { toast } from 'sonner';

/**
 * Driver Assistant — small dialog on the driver dashboard active-job card.
 * Given a driver's situation ("running late", "at pickup", etc.) it returns
 * 3 short SMS templates the driver can copy-paste.
 */
export default function DriverAssistantButton({ jobId }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const run = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    setTemplates([]);
    try {
      const out = await aiDriverAssistant(prompt.trim(), jobId);
      setTemplates(out || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI request failed');
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-orange-600 border-orange-300 hover:bg-orange-50"
        data-testid={`driver-assistant-btn-${jobId || 'generic'}`}
      >
        <Sparkles className="w-4 h-4 mr-1" />
        AI Message Helper
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-orange-500" />
              Message Helper
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-zinc-600">Your situation</label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., 'Running 10 min late due to traffic' or 'I'm at the pickup but the store is closed'"
                rows={3}
                className="text-sm"
                data-testid="driver-assistant-prompt"
              />
            </div>
            <Button
              onClick={run}
              disabled={!prompt.trim() || loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="driver-assistant-generate"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-1" /> Generate 3 Templates</>
              )}
            </Button>
            {templates.length > 0 && (
              <div className="space-y-2 pt-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Tap to copy
                </div>
                {templates.map((tpl, i) => (
                  <button
                    key={i}
                    onClick={() => copy(tpl, i)}
                    className="w-full text-left bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm text-zinc-900 hover:border-orange-400 hover:bg-orange-50 transition-colors flex justify-between items-start gap-2"
                    data-testid={`driver-template-${i}`}
                  >
                    <span className="flex-1">{tpl}</span>
                    {copiedIdx === i ? (
                      <Check className="w-4 h-4 text-green-600 shrink-0" />
                    ) : (
                      <Copy className="w-4 h-4 text-zinc-400 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
